import React, { useRef, useEffect } from 'react';

interface GhostInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
    value: string;
    hint: string;
    onChange: (value: string) => void;
}

export const GhostInput = React.forwardRef<HTMLInputElement, GhostInputProps>(({ value, hint, onChange, className, style, ...props }, ref) => {
    // We use CSS Grid to stack the Ghost Layer and the Input Layer perfectly on top of each other.
    // This solves alignment issues caused by padding/margin inheritance.

    return (
        <div
            className={className}
            style={{
                display: 'grid',
                gridTemplateAreas: '"stack"',
                width: (style as any)?.width || '100%',
                flex: (style as any)?.flex,
                margin: (style as any)?.margin,
                marginTop: (style as any)?.marginTop,
                marginBottom: (style as any)?.marginBottom,
                marginLeft: (style as any)?.marginLeft,
                marginRight: (style as any)?.marginRight,
                position: 'relative'
            }}
        >
            {/* Ghost Layer - Provides Background, Border, and Helper Text */}
            <div
                aria-hidden="true"
                style={{
                    gridArea: 'stack',
                    ...style, // Inherit all sizing/font/padding/border styles
                    margin: 0, // Container handles margin
                    color: 'transparent', // Main text invisible
                    pointerEvents: 'none',
                    whiteSpace: 'pre',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                {/* Phantom User Text */}
                <span style={{ visibility: 'hidden' }}>{value}</span>
                {/* Visible Hint */}
                <span style={{ color: 'rgba(255, 255, 255, 0.4)', visibility: 'visible', pointerEvents: 'none' }}>{hint}</span>
            </div>

            {/* Actual Input - Provides User Text */}
            <input
                {...props}
                ref={ref}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                style={{
                    gridArea: 'stack',
                    ...style,
                    margin: 0,
                    background: 'transparent',
                    borderColor: 'transparent',
                    backgroundColor: 'transparent',
                }}
                autoComplete="off"
                autoCapitalize="off"
                spellCheck="false"
            />
        </div>
    );
});

GhostInput.displayName = 'GhostInput';
